import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Heading,
  IconButton,
  Input,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiCheckCircle, FiChevronRight, FiDownload, FiPower, FiRefreshCw, FiSend, FiWifi } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';
import { getSha256 } from '@/utils/utils';
import { MQTTManager } from '@/services/mqttManager';
import apiClient, { Device } from '@/services/letsparkyClient';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';

type JsonValue = string | number | boolean | null | Record<string, unknown> | unknown[];

interface GatewayDiscoveredDevice {
  hardwareId: string;
  displayId: string;
  rssi?: number;
  /** From gateway status `devices[].allowed` when present; otherwise inferred via `allowedIds` in the UI only. */
  allowed?: boolean;
  details: Record<string, unknown>;
}

interface GatewayStatus {
  gatewayId: string;
  topic: string;
  status?: string;
  version?: string;
  type?: string;
  uptime?: number;
  lastScan?: number;
  deviceCount?: number;
  allowedIds?: string[];
  reportedDevices?: string[];
  discoveredDevices: GatewayDiscoveredDevice[];
  raw: Record<string, unknown>;
  lastSeen: Date;
  messageCount: number;
}

interface GatewayCommand {
  command: string;
  commandId: string;
  data?: JsonValue;
  key?: string;
  value?: JsonValue;
  password?: string;
}

const STATUS_TOPIC = 'parky/gateway/+/status';
const COMMAND_RESULT_TOPIC = 'parky/gateway/+/command/result';
const LAST_SCAN_WINDOW_MS = 60 * 60 * 1000;
const ACTIVE_WINDOW_MS = 2 * 60 * 1000;
const DEFAULT_BROKER_URL = 'wss://mqtt.letsparky.com';
const DEFAULT_USERNAME = 'consumer';
const DEFAULT_PASSWORD = 'rc>2uELJ9Av*';
/** Default BLE secret for allowlist lines when a discovered device has no panel match or no `ble_password`. */
const DEFAULT_DEVICE_BLE_PASSWORD = 'X10T8b97';

const COLON_SEPARATED_BLE_MAC = /^([0-9A-Fa-f]{2}(?::[0-9A-Fa-f]{2}){5})=(.+)$/;

const parseNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  }
  return undefined;
};

const parseArray = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item));
  }
  return undefined;
};

const parseDeviceIdentifiers = (value: unknown, includeRssiLabel = false): string[] | undefined => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string' || typeof item === 'number') return String(item);
        if (!item || typeof item !== 'object') return '';
        const record = item as Record<string, unknown>;
        const mac =
          record.mac ??
          record.macAddress ??
          record.address ??
          record.identifier ??
          record.id ??
          record.deviceId;
        if (!(typeof mac === 'string' || typeof mac === 'number')) return '';
        const macText = String(mac);
        if (!includeRssiLabel) return macText;

        const rssi = parseNumber(record.bleSignal);
        return rssi !== undefined ? `${macText} (rssi=${rssi})` : macText;
      })
      .filter(Boolean);
  }
  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>);
  }
  return undefined;
};

const extractGatewayIdFromTopic = (topic: string): string | null => {
  const match = topic.match(/^parky\/gateway\/([^/]+)\//);
  return match ? match[1] : null;
};

const normalizeTimestampMs = (value: unknown): number | undefined => {
  const parsed = parseNumber(value);
  if (!parsed) return undefined;

  // If the timestamp looks like seconds, convert to milliseconds.
  return parsed < 1_000_000_000_000 ? parsed * 1000 : parsed;
};

const normalizeBleHardwareId = (value: string) => value.replace(/[^a-fA-F0-9]/g, '').toUpperCase();

const extractAllowlistIdPart = (entry: string): string => {
  const trimmed = entry.trim();
  if (!trimmed) return '';
  const colonPair = trimmed.match(COLON_SEPARATED_BLE_MAC);
  if (colonPair) return colonPair[1];
  const eqIdx = trimmed.indexOf('=');
  if (eqIdx !== -1) return trimmed.slice(0, eqIdx).trim();
  return trimmed;
};

const isDiscoveredDeviceAllowed = (disc: GatewayDiscoveredDevice, allowedIds?: string[]): boolean => {
  if (!allowedIds?.length) return false;
  for (const raw of allowedIds) {
    const idPart = extractAllowlistIdPart(String(raw));
    if (!idPart) continue;
    const hw = normalizeBleHardwareId(idPart);
    if (hw && disc.hardwareId === hw) return true;
    if (disc.displayId.trim().toLowerCase() === idPart.toLowerCase()) return true;
  }
  return false;
};

const parseDeviceAllowedFlag = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

const extractMacFromDiscoveryRecord = (record: Record<string, unknown>): string | undefined => {
  const mac =
    record.mac ??
    record.macAddress ??
    record.address ??
    record.identifier ??
    record.id ??
    record.deviceId;
  if (typeof mac === 'string' || typeof mac === 'number') return String(mac);
  return undefined;
};

const parseDiscoveredDevices = (
  payload: Record<string, unknown>,
  previous?: GatewayStatus
): GatewayDiscoveredDevice[] => {
  const rawDevices = payload.devices;
  if (Array.isArray(rawDevices) && rawDevices.length > 0) {
    const out: GatewayDiscoveredDevice[] = [];
    for (const item of rawDevices) {
      if (typeof item === 'string' || typeof item === 'number') {
        const displayId = String(item);
        const hardwareId = normalizeBleHardwareId(displayId);
        if (hardwareId) out.push({ hardwareId, displayId, details: {} });
        continue;
      }
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>;
        const displayId = extractMacFromDiscoveryRecord(record);
        if (!displayId) continue;
        const hardwareId = normalizeBleHardwareId(displayId);
        if (!hardwareId) continue;
        const rssi = parseNumber(record.bleSignal ?? record.rssi);
        const allowed = parseDeviceAllowedFlag(record.allowed);
        out.push({ hardwareId, displayId, rssi, allowed, details: record });
      }
    }
    return out.length ? out : previous?.discoveredDevices ?? [];
  }

  const idsList = parseArray(payload.ids);
  if (idsList?.length) {
    return idsList
      .map((id) => {
        const hardwareId = normalizeBleHardwareId(id);
        return hardwareId ? { hardwareId, displayId: id, details: {} as Record<string, unknown> } : null;
      })
      .filter((x): x is GatewayDiscoveredDevice => x !== null);
  }

  return previous?.discoveredDevices ?? [];
};

const buildGatewayStatus = (
  gatewayId: string,
  topic: string,
  payload: Record<string, unknown>,
  previous?: GatewayStatus
): GatewayStatus => {
  const idsList = parseDeviceIdentifiers(payload.ids);
  const devicesList = parseDeviceIdentifiers(payload.devices, true);
  const discoveredDevices = parseDiscoveredDevices(payload, previous);

  return {
  gatewayId,
  topic,
  status: typeof payload.status === 'string' ? payload.status : previous?.status,
  version: typeof payload.version === 'string' ? payload.version : previous?.version,
  type: typeof payload.type === 'string' ? payload.type : previous?.type,
  uptime: parseNumber(payload.uptime) ?? previous?.uptime,
  lastScan: parseNumber(payload.lastScan) ?? previous?.lastScan,
  reportedDevices:
    idsList ??
    devicesList ??
    previous?.reportedDevices,
  deviceCount:
    parseNumber(payload.deviceCount) ??
    idsList?.length ??
    devicesList?.length ??
    discoveredDevices.length ??
    previous?.deviceCount,
  allowedIds: parseArray(payload.allowedIds) ?? previous?.allowedIds,
  discoveredDevices,
  raw: { ...(previous?.raw ?? {}), ...payload },
  lastSeen: new Date(),
  messageCount: (previous?.messageCount ?? 0) + 1,
  };
};

const Gateways: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const { user, token } = useAuthStore();
  const isSystem = user?.role === 'SYSTEM';
  const mqttRef = useRef<MQTTManager | null>(null);

  const formatRelativeTime = useMemo(() => {
    return (date: Date) => {
      const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
      if (seconds < 60) return t('gateways.relativeSeconds', { count: seconds });
      if (seconds < 3600) return t('gateways.relativeMinutes', { count: Math.floor(seconds / 60) });
      return t('gateways.relativeHours', { count: Math.floor(seconds / 3600) });
    };
  }, [t]);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedBg = useColorModeValue('gray.50', 'gray.700');

  const [brokerUrl, setBrokerUrl] = useState(DEFAULT_BROKER_URL);
  const [username, setUsername] = useState(DEFAULT_USERNAME);
  const [password, setPassword] = useState(DEFAULT_PASSWORD);
  const [connected, setConnected] = useState(false);

  const [gatewayMap, setGatewayMap] = useState<Record<string, GatewayStatus>>({});
  const [selectedGatewayId, setSelectedGatewayId] = useState('');
  const [tick, setTick] = useState(0);
  const [panelDevices, setPanelDevices] = useState<Device[]>([]);

  const [configKey, setConfigKey] = useState('GATEWAY_ID');
  const [configValue, setConfigValue] = useState('');
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [deviceCommand, setDeviceCommand] = useState<'SET_DEVICES' | 'ADD_DEVICES' | 'REMOVE_DEVICES'>('ADD_DEVICES');
  const [deviceLines, setDeviceLines] = useState('');
  const [defaultDeviceBlePassword, setDefaultDeviceBlePassword] = useState(DEFAULT_DEVICE_BLE_PASSWORD);
  const [executeCommand, setExecuteCommand] = useState('');
  const [firmwareBinUrl, setFirmwareBinUrl] = useState('');
  const [customCommand, setCustomCommand] = useState('SET');
  const [customDataJson, setCustomDataJson] = useState('{\n  "key": "PING",\n  "value": "1"\n}');
  const [rawCommandJson, setRawCommandJson] = useState('{\n  "command": "SET_CONFIG",\n  "data": {\n    "key": "SAMPLE_KEY",\n    "value": "sampleValue"\n  }\n}');

  const [publishHistory, setPublishHistory] = useState<Array<{ at: Date; topic: string; payload: string; direction: 'outgoing' | 'incoming'; gatewayId: string }>>([]);

  useEffect(() => {
    const interval = window.setInterval(() => setTick((prev) => prev + 1), 10000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!token) return;

    const loadDevices = async () => {
      try {
        apiClient.client.setToken(token);
        const params: Record<string, string> = {};
        if (!isSystem && user?.tenantId) {
          params.tenant_id = user.tenantId;
        }
        const response = await apiClient.devices.listPaginated({ page: 1, limit: 5000 }, params);
        setPanelDevices(response.data);
      } catch (error) {
        console.error('Gateways: failed to load devices for BLE matching', error);
      }
    };

    void loadDevices();
  }, [token, isSystem, user?.tenantId]);

  const panelDeviceByBleId = useMemo(() => {
    const map = new Map<string, Device>();
    for (const d of panelDevices) {
      const macOnly = normalizeBleHardwareId(d.mac_address || '');
      const serialOnly = normalizeBleHardwareId(d.serial_no || '');
      if (macOnly) map.set(macOnly, d);
      if (serialOnly) map.set(serialOnly, d);
    }
    return map;
  }, [panelDevices]);

  const gateways = useMemo(() => {
    tick;
    return Object.values(gatewayMap)
      .sort((a, b) => b.lastSeen.getTime() - a.lastSeen.getTime())
      .map((gateway) => ({
        ...gateway,
        isActive: Date.now() - gateway.lastSeen.getTime() <= ACTIVE_WINDOW_MS,
      }));
  }, [gatewayMap, tick]);

  const selectedGateway = useMemo(
    () => gateways.find((item) => item.gatewayId === selectedGatewayId) ?? null,
    [gateways, selectedGatewayId]
  );

  const ensureSelectedGateway = (nextMap: Record<string, GatewayStatus>) => {
    if (!selectedGatewayId) {
      const firstId = Object.keys(nextMap)[0];
      if (firstId) setSelectedGatewayId(firstId);
      return;
    }

    if (!nextMap[selectedGatewayId]) {
      const nextId = Object.keys(nextMap)[0] ?? '';
      setSelectedGatewayId(nextId);
    }
  };

  const disconnect = () => {
    if (mqttRef.current) {
      mqttRef.current.disconnect();
      mqttRef.current = null;
    }
    setConnected(false);
  };

  const connect = () => {
    if (mqttRef.current) {
      disconnect();
    }

    const userInstanceHash = getSha256(
      `${window.navigator.userAgent}${window.navigator.hardwareConcurrency}${window.navigator.language}`
    );

    const manager = new MQTTManager(brokerUrl, `panel_gateway_${userInstanceHash}_${new Date().getDay()}`, username, password);

    manager.onConnect(() => {
      setConnected(true);
      [STATUS_TOPIC, COMMAND_RESULT_TOPIC].forEach((topicPattern) => {
        manager.subscribe(topicPattern, (err) => {
          if (err) {
            toast({
              title: t('gateways.toast.subscriptionFailed'),
              description: `${topicPattern}: ${err.message}`,
              status: 'error',
              duration: 4000,
              isClosable: true,
            });
          }
        });
      });

      toast({
        title: t('gateways.toast.connected'),
        description: t('gateways.toast.connectedDesc', { statusTopic: STATUS_TOPIC, resultTopic: COMMAND_RESULT_TOPIC }),
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    });

    manager.onDisconnect(() => {
      setConnected(false);
    });

    manager.onError((error) => {
      toast({
        title: t('gateways.toast.mqttError'),
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    });

    manager.onMessage((topic, payload) => {
      if (topic.match(/^parky\/gateway\/[^/]+\/command\/result$/)) {
        const gatewayId = extractGatewayIdFromTopic(topic);
        if (!gatewayId) return;
        setPublishHistory((prev) =>
          [{ at: new Date(), topic, payload, direction: 'incoming' as const, gatewayId }, ...prev].slice(0, 40)
        );
        return;
      }

      const match = topic.match(/^parky\/gateway\/([^/]+)\/status$/);
      if (!match) return;

      const gatewayId = match[1];
      let parsedPayload: Record<string, unknown>;
      try {
        parsedPayload = JSON.parse(payload);
      } catch {
        console.error('Failed to parse payload', payload);
        parsedPayload = { rawPayload: payload };
      }
      const normalizedLastScan = normalizeTimestampMs(parsedPayload.lastScan);
      if (normalizedLastScan !== 0 && normalizedLastScan && (Date.now() - normalizedLastScan > LAST_SCAN_WINDOW_MS)) {
        return;
      }

      setGatewayMap((prev) => {
        const next = {
          ...prev,
          [gatewayId]: buildGatewayStatus(gatewayId, topic, parsedPayload, prev[gatewayId]),
        };
        ensureSelectedGateway(next);
        return next;
      });
    });

    mqttRef.current = manager;
    manager.connect();
  };

  useEffect(() => () => disconnect(), []);

  const publishCommand = (gatewayId: string, payload: GatewayCommand | Record<string, unknown>) => {
    if (!mqttRef.current || !mqttRef.current.isConnected()) {
      toast({
        title: t('gateways.toast.notConnected'),
        description: t('gateways.toast.notConnectedDesc'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const topic = `parky/gateway/${gatewayId}/command`;
    const message = JSON.stringify(payload);

    mqttRef.current.publish(topic, message, (err) => {
      if (err) {
        toast({
          title: t('gateways.toast.publishFailed'),
          description: err.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      setPublishHistory((prev) => [{ at: new Date(), topic, payload: message, direction: 'outgoing' as const, gatewayId }, ...prev].slice(0, 40));
      toast({
        title: t('gateways.toast.commandSent'),
        description: `${topic}`,
        status: 'success',
        duration: 1800,
        isClosable: true,
      });
    });
  };

  const requireGateway = () => {
    if (selectedGateway) return selectedGateway;
    toast({ title: t('gateways.toast.selectGateway'), status: 'warning', duration: 2000 });
    return null;
  };

  const parseDeviceLinesToPayload = (rawValue: string) => {
    const lines = rawValue
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const map: Record<string, JsonValue> = {};
    lines.forEach((line) => {
      const colonPair = line.match(COLON_SEPARATED_BLE_MAC);
      if (colonPair) {
        const id = colonPair[1];
        const secret = colonPair[2].trim();
        if (id) map[id] = secret || null;
        return;
      }
      const separatorIdx = line.indexOf('=');
      if (separatorIdx === -1) {
        map[line] = null;
        return;
      }
      const id = line.slice(0, separatorIdx).trim();
      const secret = line.slice(separatorIdx + 1).trim();
      if (id) map[id] = secret || null;
    });
    return map;
  };

  const handleSendConfig = () => {
    const gateway = requireGateway();
    if (!gateway) return;
    publishCommand(gateway.gatewayId, {
      command: 'SET_CONFIG',
      commandId: crypto.randomUUID(),
      data: { key: configKey, value: configValue },
    });
  };

  const handleSendWifi = () => {
    const gateway = requireGateway();
    if (!gateway) return;
    const ssid = wifiSsid.trim();
    if (!ssid) {
      toast({
        title: t('gateways.toast.ssidRequired'),
        description: t('gateways.toast.ssidHelp'),
        status: 'warning',
        duration: 2800,
        isClosable: true,
      });
      return;
    }
    publishCommand(gateway.gatewayId, {
      command: 'SET_CONFIG',
      commandId: crypto.randomUUID(),
      ssid,
      password: wifiPassword
    });
  };

  const buildAllowlistLine = (displayId: string, password: string) => {
    const id = displayId.trim();
    // if (/^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/.test(id)) {
    //   return `${id}:${password}`;
    // }
    return `${id}=${password}`;
  };

  const fillAllowedDevicesFromNearby = () => {
    const gateway = requireGateway();
    if (!gateway) return;
    if (gateway.discoveredDevices.length === 0) {
      toast({
        title: t('gateways.toast.noNearbyDevices'),
        description: t('gateways.toast.noNearbyHelp'),
        status: 'info',
        duration: 2800,
        isClosable: true,
      });
      return;
    }
    const seen = new Set<string>();
    const lines: string[] = [];
    for (const disc of gateway.discoveredDevices) {
      if (seen.has(disc.hardwareId)) continue;
      seen.add(disc.hardwareId);
      const matched = panelDeviceByBleId.get(disc.hardwareId);
      const pwd = (matched?.ble_password && matched.ble_password.trim()) || defaultDeviceBlePassword;
      lines.push(buildAllowlistLine(disc.displayId, pwd));
    }
    setDeviceLines(lines.join('\n'));
  };

  const handleSendDevices = () => {
    const gateway = requireGateway();
    if (!gateway) return;

    const parsedMap = parseDeviceLinesToPayload(deviceLines);
    if (Object.keys(parsedMap).length === 0) {
      toast({
        title: t('gateways.toast.noDevicesProvided'),
        description: t('gateways.toast.noDevicesHelp'),
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const data =
      deviceCommand === 'REMOVE_DEVICES'
        ? Object.keys(parsedMap).reduce<Record<string, null>>((acc, id) => {
            acc[id] = null;
            return acc;
          }, {})
        : parsedMap;

    publishCommand(gateway.gatewayId, {
      command: deviceCommand,
      commandId: crypto.randomUUID(),
      data,
    });
  };

  const handleExecute = () => {
    const gateway = requireGateway();
    if (!gateway) return;
    if (!executeCommand.trim()) {
      toast({ title: t('gateways.toast.commandEmpty'), status: 'warning', duration: 2000 });
      return;
    }
    publishCommand(gateway.gatewayId, {
      command: 'EXECUTE',
      commandId: crypto.randomUUID(),
      data: executeCommand.trim(),
    });
  };

  const handleFirmwareUpgrade = () => {
    const gateway = requireGateway();
    if (!gateway) return;
    const url = firmwareBinUrl.trim();
    if (!url) {
      toast({ title: t('gateways.toast.firmwareUrlRequired'), status: 'warning', duration: 2000, isClosable: true });
      return;
    }
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      toast({
        title: t('gateways.toast.invalidUrl'),
        description: t('gateways.toast.invalidUrlHelp'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      toast({
        title: t('gateways.toast.invalidUrlScheme'),
        description: t('gateways.toast.invalidUrlSchemeHelp'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    publishCommand(gateway.gatewayId, { command: 'firmwareupgrade', data: url });
  };

  const handleSendCustomCommand = () => {
    const gateway = requireGateway();
    if (!gateway) return;
    if (!customCommand.trim()) {
      toast({ title: t('gateways.toast.commandNameEmpty'), status: 'warning', duration: 2000 });
      return;
    }
    try {
      const parsedData = JSON.parse(customDataJson) as JsonValue;
      publishCommand(gateway.gatewayId, {
        command: customCommand.trim(),
        commandId: crypto.randomUUID(),
        data: parsedData,
      });
    } catch {
      toast({
        title: t('gateways.toast.invalidCustomJson'),
        status: 'error',
        duration: 2500,
        isClosable: true,
      });
    }
  };

  const handleRawCommandSend = () => {
    if (!selectedGateway) return;
    try {
      const payload = JSON.parse(rawCommandJson) as Record<string, unknown>;
      publishCommand(selectedGateway.gatewayId, payload);
    } catch {
      toast({
        title: t('gateways.toast.invalidJson'),
        description: t('gateways.toast.invalidJsonHelp'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const sendQuickCommand = (gatewayId: string, cmd: GatewayCommand) => {
    publishCommand(gatewayId, cmd);
  };

  const selectedGatewayHistory = useMemo(
    () => publishHistory.filter((item) => item.gatewayId === selectedGatewayId),
    [publishHistory, selectedGatewayId]
  );

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">{t('gateways.title')}</Heading>
        <Badge colorScheme={connected ? 'green' : 'red'} fontSize="0.85rem" px={3} py={1} borderRadius="md">
          {connected ? t('gateways.connected') : t('gateways.disconnected')}
        </Badge>
      </Flex>

      <Grid templateColumns={{ base: '1fr', xl: '1.2fr 1fr' }} gap={6}>
        <GridItem>
          <VStack spacing={6} align="stretch">
            <Box borderWidth="1px" borderColor={borderColor} bg={cardBg} borderRadius="lg" p={5}>
              <Heading size="sm" mb={4}>{t('gateways.mqttConnection')}</Heading>
              <Grid templateColumns={{ base: '1fr', md: '2fr 1fr 1fr auto' }} gap={3}>
                <Input value={brokerUrl} onChange={(e) => setBrokerUrl(e.target.value)} isDisabled={connected} placeholder={t('gateways.brokerUrl')} />
                <Input value={username} onChange={(e) => setUsername(e.target.value)} isDisabled={connected} placeholder={t('gateways.username')} />
                <Input value={password} onChange={(e) => setPassword(e.target.value)} isDisabled={connected} placeholder={t('gateways.password')} type="password" />
                {connected ? (
                  <Button colorScheme="red" onClick={disconnect}>{t('gateways.disconnect')}</Button>
                ) : (
                  <Button colorScheme="blue" onClick={connect}>{t('gateways.connect')}</Button>
                )}
              </Grid>
              <Text mt={3} fontSize="sm" color="gray.500">
                {t('gateways.listeningTopics')}{' '}
                <Text as="span" fontFamily="mono">{STATUS_TOPIC}</Text>
                {' , '}
                <Text as="span" fontFamily="mono">{COMMAND_RESULT_TOPIC}</Text>
              </Text>
            </Box>

            <Box borderWidth="1px" borderColor={borderColor} bg={cardBg} borderRadius="lg" p={5}>
              <Flex align="center" justify="space-between" mb={4}>
                <Heading size="sm">{t('gateways.activeGateways')}</Heading>
                <Badge colorScheme="blue">{t('gateways.discoveredCount', { count: gateways.length })}</Badge>
              </Flex>
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>{t('gateways.colGateway')}</Th>
                      <Th>{t('gateways.colStatus')}</Th>
                      <Th>{t('gateways.colVersion')}</Th>
                      <Th isNumeric>{t('gateways.colUptime')}</Th>
                      <Th isNumeric>{t('gateways.colDevices')}</Th>
                      <Th>{t('gateways.colLastSeen')}</Th>
                      <Th>{t('gateways.quickActions')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {gateways.length === 0 ? (
                      <Tr>
                        <Td colSpan={7}>
                          <Text color="gray.500">{t('gateways.noStatusYet')}</Text>
                        </Td>
                      </Tr>
                    ) : (
                      gateways.map((gateway) => (
                        <Tr
                          key={gateway.gatewayId}
                          bg={gateway.gatewayId === selectedGatewayId ? mutedBg : 'transparent'}
                          cursor="pointer"
                          onClick={() => setSelectedGatewayId(gateway.gatewayId)}
                        >
                          <Td>
                            <HStack spacing={2}>
                              <Text fontFamily="mono">{gateway.gatewayId}</Text>
                              {gateway.isActive ? <FiCheckCircle color="#38A169" /> : null}
                            </HStack>
                          </Td>
                          <Td>
                            <Badge colorScheme={gateway.isActive ? 'green' : 'gray'}>{gateway.status ?? (gateway.isActive ? t('gateways.statusOnline') : t('gateways.statusStale'))}</Badge>
                          </Td>
                          <Td>{gateway.version ?? '-'}</Td>
                          <Td isNumeric>{gateway.uptime ?? '-'}</Td>
                          <Td isNumeric>{gateway.deviceCount ?? '-'}</Td>
                          <Td>{formatRelativeTime(gateway.lastSeen)}</Td>
                          <Td>
                            <HStack spacing={1}>
                              <Tooltip label={t('gateways.pingTooltip')}>
                                <IconButton
                                  aria-label={t('gateways.pingAria')}
                                  icon={<FiRefreshCw />}
                                  size="xs"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    sendQuickCommand(gateway.gatewayId, { command: 'SET', commandId: `${Date.now()}`, data: { key: 'PING', value: Date.now() } });
                                  }}
                                />
                              </Tooltip>
                              <Tooltip label={t('gateways.shutdownTooltip')}>
                                <IconButton
                                  aria-label={t('gateways.shutdownAria')}
                                  icon={<FiPower />}
                                  size="xs"
                                  colorScheme="red"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    sendQuickCommand(gateway.gatewayId, { command: 'SHUTDOWN', commandId: `${Date.now()}` });
                                  }}
                                />
                              </Tooltip>
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </VStack>
        </GridItem>

        <GridItem>
          <VStack spacing={6} align="stretch">
            <Box borderWidth="1px" borderColor={borderColor} bg={cardBg} borderRadius="lg" p={5}>
              <Heading size="sm" mb={4}>{t('gateways.management')}</Heading>
              {!selectedGateway ? (
                <Text color="gray.500">{t('gateways.selectGatewayHint')}</Text>
              ) : (
                <VStack align="stretch" spacing={4}>
                  <Text fontSize="sm">
                    {t('gateways.target')}{' '}
                    <Text as="span" fontWeight="bold" fontFamily="mono">{selectedGateway.gatewayId}</Text>
                  </Text>
                  <Box
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="lg"
                    overflow="hidden"
                    bg={cardBg}
                  >
                    <Box px={4} py={3} borderBottomWidth="1px" borderColor={borderColor}>
                      <Text fontWeight="semibold" fontSize="sm">
                        {t('gateways.nearbyDevices')}
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {t('gateways.nearbyDevicesHelp')}
                      </Text>
                    </Box>
                    {selectedGateway.discoveredDevices.length === 0 ? (
                      <Box px={4} py={6}>
                        <Text fontSize="sm" color="gray.500" textAlign="center">
                          {t('gateways.noDevicesInLastStatus')}
                        </Text>
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table size="sm" variant="simple">
                          <Thead>
                            <Tr>
                              <Th py={3} borderColor={borderColor}>
                                {t('gateways.colAddress')}
                              </Th>
                              <Th py={3} borderColor={borderColor} w="44px">
                                {t('gateways.colOwn')}
                              </Th>
                              <Th py={3} isNumeric borderColor={borderColor} w="72px">
                                {t('gateways.colSignal')}
                              </Th>
                              <Th py={3} borderColor={borderColor}>
                                {t('gateways.colInPanel')}
                              </Th>
                              <Th py={3} borderColor={borderColor} display={{ base: 'none', md: 'table-cell' }}>
                                {t('gateways.colRawScan')}
                              </Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {selectedGateway.discoveredDevices.map((disc) => {
                              const matched = panelDeviceByBleId.get(disc.hardwareId);
                              const allowedIds = selectedGateway.allowedIds;
                              const allowedColumn: 'yes' | 'no' | 'neutral' =
                                disc.allowed === true
                                  ? 'yes'
                                  : disc.allowed === false
                                    ? 'no'
                                    : isDiscoveredDeviceAllowed(disc, allowedIds)
                                      ? 'yes'
                                      : 'neutral';
                              const discoveryPreview =
                                Object.keys(disc.details).length === 0
                                  ? '—'
                                  : JSON.stringify(disc.details);
                              return (
                                <Tr key={`${disc.hardwareId}-${disc.displayId}`} _hover={{ bg: mutedBg }}>
                                  <Td fontFamily="mono" fontSize="xs" verticalAlign="middle" borderColor={borderColor}>
                                    {disc.displayId}
                                  </Td>
                                  <Td verticalAlign="middle" borderColor={borderColor} style={{ textAlign: 'center' }}>
                                    {allowedColumn === 'yes' ? (
                                      <Badge colorScheme="green" variant="subtle" fontWeight="medium" display="inline-flex" alignItems="center" gap={1}>
                                        <FiCheckCircle />
                                        
                                      </Badge>
                                    ) : allowedColumn === 'no' ? (
                                      <Badge colorScheme="gray" variant="subtle" fontWeight="medium">
                                        
                                      </Badge>
                                    ) : (
                                      <Text as="span" fontSize="sm" color="gray.400">
                                        —
                                      </Text>
                                    )}
                                  </Td>
                                  <Td isNumeric fontSize="sm" verticalAlign="middle" borderColor={borderColor}>
                                    {disc.rssi !== undefined ? (
                                      <Badge
                                        colorScheme={disc.rssi > -70 ? 'green' : disc.rssi > -85 ? 'yellow' : 'orange'}
                                        variant="subtle"
                                        fontWeight="normal"
                                      >
                                        {t('gateways.signalDbm', { value: disc.rssi })}
                                      </Badge>
                                    ) : (
                                      <Text as="span" color="gray.400">
                                        —
                                      </Text>
                                    )}
                                  </Td>
                                  <Td verticalAlign="middle" borderColor={borderColor}>
                                    {matched ? (
                                      <Button
                                        as={RouterLink}
                                        to={`/devices?deviceId=${matched.id}`}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="blue"
                                        rightIcon={<FiChevronRight />}
                                        fontWeight="medium"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {matched.nick_name || matched.serial_no}
                                      </Button>
                                    ) : (
                                      <HStack spacing={2}>
                                        <Text color="gray.500" fontSize="sm">
                                          {t('gateways.noMatch')}
                                        </Text>
                                        <Tooltip label={t('gateways.whyTooltip')} fontSize="xs" hasArrow>
                                          <Text fontSize="xs" color="gray.400" cursor="help" borderBottom="1px dotted" borderColor="gray.400">
                                            {t('gateways.whyLink')}
                                          </Text>
                                        </Tooltip>
                                      </HStack>
                                    )}
                                  </Td>
                                  <Td
                                    maxW={{ md: '240px', lg: '280px' }}
                                    display={{ base: 'none', md: 'table-cell' }}
                                    verticalAlign="middle"
                                    borderColor={borderColor}
                                  >
                                    {discoveryPreview === '—' ? (
                                      <Text fontSize="xs" color="gray.400">
                                        —
                                      </Text>
                                    ) : (
                                      <Tooltip label={discoveryPreview} openDelay={300} maxW="md" hasArrow>
                                        <Text
                                          fontSize="xs"
                                          fontFamily="mono"
                                          noOfLines={2}
                                          whiteSpace="pre-wrap"
                                          wordBreak="break-word"
                                          cursor="default"
                                        >
                                          {discoveryPreview}
                                        </Text>
                                      </Tooltip>
                                    )}
                                  </Td>
                                </Tr>
                              );
                            })}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>

                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                    <Text fontWeight="semibold" mb={2}>{t('gateways.setConfig')}</Text>
                    <Grid templateColumns="1fr 1fr auto" gap={2}>
                      <Input value={configKey} onChange={(e) => setConfigKey(e.target.value)} placeholder={t('gateways.configKey')} />
                      <Input value={configValue} onChange={(e) => setConfigValue(e.target.value)} placeholder={t('gateways.configValue')} />
                      <Button colorScheme="blue" onClick={handleSendConfig}>{t('gateways.apply')}</Button>
                    </Grid>
                  </Box>

                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                    <Text fontWeight="semibold" mb={2}>{t('gateways.setWifi')}</Text>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      {t('gateways.setWifiHelp')}
                    </Text>
                    <Grid templateColumns={{ base: '1fr', md: '1fr 1fr auto' }} gap={2} alignItems={{ base: 'stretch', md: 'end' }}>
                      <Input
                        value={wifiSsid}
                        onChange={(e) => setWifiSsid(e.target.value)}
                        placeholder={t('gateways.networkName')}
                        autoComplete="off"
                      />
                      <Input
                        type="password"
                        value={wifiPassword}
                        onChange={(e) => setWifiPassword(e.target.value)}
                        placeholder={t('gateways.wifiPasswordOptional')}
                        autoComplete="new-password"
                      />
                      <Button colorScheme="cyan" leftIcon={<FiWifi />} onClick={handleSendWifi}>
                        {t('gateways.setWifiButton')}
                      </Button>
                    </Grid>
                  </Box>

                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                    <Text fontWeight="semibold" mb={2}>{t('gateways.allowedDevices')}</Text>
                    <HStack mb={2} spacing={2} align="stretch" flexWrap="wrap">
                      <Select value={deviceCommand} onChange={(e) => setDeviceCommand(e.target.value as 'SET_DEVICES' | 'ADD_DEVICES' | 'REMOVE_DEVICES')}>
                        <option value="SET_DEVICES">SET_DEVICES</option>
                        <option value="ADD_DEVICES">ADD_DEVICES</option>
                        <option value="REMOVE_DEVICES">REMOVE_DEVICES</option>
                      </Select>
                      <Input
                        maxW={{ md: '200px' }}
                        size="sm"
                        fontFamily="mono"
                        value={defaultDeviceBlePassword}
                        onChange={(e) => setDefaultDeviceBlePassword(e.target.value)}
                        placeholder={t('gateways.defaultBlePassword')}
                        aria-label={t('gateways.defaultBlePasswordAria')}
                      />
                      <Button size="sm" variant="outline" onClick={fillAllowedDevicesFromNearby}>
                        {t('gateways.fillFromNearby')}
                      </Button>
                      <Button colorScheme="blue" onClick={handleSendDevices}>{t('gateways.send')}</Button>
                    </HStack>
                    <Textarea
                      value={deviceLines}
                      onChange={(e) => setDeviceLines(e.target.value)}
                      rows={4}
                      fontFamily="mono"
                      placeholder={t('gateways.deviceLinesPlaceholder', {
                        exampleLine: `AA:BB:CC:DD:EE:FF=${DEFAULT_DEVICE_BLE_PASSWORD}`,
                        exampleLine2: `OtherId=${DEFAULT_DEVICE_BLE_PASSWORD}`,
                      })}
                    />
                    
                  </Box>

                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                    <Text fontWeight="semibold" mb={2}>{t('gateways.executeCommand')}</Text>
                    <Grid templateColumns="1fr auto" gap={2}>
                      <Input value={executeCommand} onChange={(e) => setExecuteCommand(e.target.value)} placeholder={t('gateways.executePlaceholder')} />
                      <Button colorScheme="blue" leftIcon={<FiSend />} onClick={handleExecute}>{t('gateways.execute')}</Button>
                    </Grid>
                  </Box>

                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                    <Text fontWeight="semibold" mb={2}>{t('gateways.upgradeFirmware')}</Text>
                    <Grid templateColumns="1fr auto" gap={2}>
                      <Input
                        type="url"
                        value={firmwareBinUrl}
                        onChange={(e) => setFirmwareBinUrl(e.target.value)}
                        placeholder={t('gateways.firmwarePlaceholder')}
                        fontFamily="mono"
                        fontSize="sm"
                      />
                      <Button colorScheme="orange" leftIcon={<FiDownload />} onClick={handleFirmwareUpgrade}>
                        {t('gateways.upgrade')}
                      </Button>
                    </Grid>
                  </Box>

                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                    <Text fontWeight="semibold" mb={2}>{t('gateways.quickPowerActions')}</Text>
                    <ButtonGroup>
                      <Button
                        size="sm"
                        leftIcon={<FiRefreshCw />}
                        onClick={() =>
                          publishCommand(selectedGateway.gatewayId, {
                            command: 'SET',
                            commandId: crypto.randomUUID(),
                            data: { key: 'PING', value: Date.now() },
                          })
                        }
                      >
                        {t('gateways.ping')}
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        leftIcon={<FiPower />}
                        onClick={() =>
                          publishCommand(selectedGateway.gatewayId, {
                            command: 'SHUTDOWN',
                            commandId: crypto.randomUUID(),
                          })
                        }
                      >
                        {t('gateways.shutdown')}
                      </Button>
                    </ButtonGroup>
                  </Box>

                  {/* <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                    <Text fontWeight="semibold" mb={2}>Custom Command</Text>
                    <Grid templateColumns="1fr auto" gap={2} mb={2}>
                      <Input value={customCommand} onChange={(e) => setCustomCommand(e.target.value)} placeholder="Command name" />
                      <Button onClick={handleSendCustomCommand} colorScheme="blue">Send</Button>
                    </Grid>
                    <Textarea value={customDataJson} onChange={(e) => setCustomDataJson(e.target.value)} rows={4} fontFamily="mono" />
                  </Box> */}
                </VStack>
              )}
            </Box>

            {/* <Box borderWidth="1px" borderColor={borderColor} bg={cardBg} borderRadius="lg" p={5}>
              <Heading size="sm" mb={3}>Raw JSON Publisher</Heading>
              <Textarea value={rawCommandJson} onChange={(e) => setRawCommandJson(e.target.value)} rows={7} fontFamily="mono" mb={3} />
              <Button colorScheme="purple" onClick={handleRawCommandSend} leftIcon={<FiSend />} isDisabled={!selectedGateway}>
                Publish Raw JSON
              </Button>
            </Box> */}

            <Box borderWidth="1px" borderColor={borderColor} bg={cardBg} borderRadius="lg" p={5}>
              <Heading size="sm" mb={3}>{t('gateways.publishLog')}</Heading>
              <VStack align="stretch" spacing={3} maxH="260px" overflowY="auto">
                {!selectedGatewayId ? (
                  <Text color="gray.500" fontSize="sm">{t('gateways.selectGatewayForLogs')}</Text>
                ) : selectedGatewayHistory.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">{t('gateways.noLogsYet')}</Text>
                ) : (
                  selectedGatewayHistory.map((item, idx) => (
                    <Box key={`${item.at.toISOString()}-${idx}`} bg={mutedBg} borderRadius="md" p={3}>
                      <HStack justify="space-between" align="center" mb={1}>
                        <Text fontSize="xs" color="gray.500">{item.at.toLocaleTimeString()}</Text>
                        <Badge
                          size="sm"
                          colorScheme={item.direction === 'incoming' ? 'green' : 'blue'}
                          variant="subtle"
                        >
                          {item.direction === 'incoming' ? t('gateways.logResult') : t('gateways.logSent')}
                        </Badge>
                      </HStack>
                      <Text fontFamily="mono" fontSize="xs" mb={1}>{item.topic}</Text>
                      <Divider my={1} />
                      <Text fontFamily="mono" fontSize="xs" whiteSpace="pre-wrap" wordBreak="break-word">{item.payload}</Text>
                    </Box>
                  ))
                )}
              </VStack>
            </Box>
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default Gateways;
